import { isEqual, isObject } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";

import type { RootState } from "shared/types";
import type { AppDispatch } from "store";

// Use throughout the app instead of plain `useDispatch` and `useSelector`
// See https://redux-toolkit.js.org/tutorials/typescript#define-typed-hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export function objectEqualityFn<T>(
  a: T | undefined,
  b: T | undefined,
  properties?: (keyof T)[] | undefined,
): boolean {
  if (a && b) {
    if (!isObject(a) || !isObject(b)) {
      throw new Error("objectEqualityFn can only be used with objects");
    }
    if (properties && properties.length > 0) {
      return properties.every((property) => isEqual(a[property], b[property]));
    }
    const keysA = Object.keys(a) as (keyof T)[];
    keysA.sort();
    const keysB = Object.keys(b) as (keyof T)[];
    keysB.sort();
    return keysA.join(",") === keysB.join(",") && keysA.every((key) => a[key] === b[key]);
  }
  return a === b;
}

export function arrayEqualityFn<T>(a: T[], b: T[], properties?: (keyof T)[] | undefined): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    throw new Error("arrayEqualityFn can only be used with arrays");
  }
  if (a.length !== b.length) {
    return false;
  }
  if (properties && properties.length > 0) {
    return a.every((item, index) => objectEqualityFn(item, b[index], properties));
  }
  if (a.every((item) => isObject(item))) {
    return a.every((item, index) => objectEqualityFn(item, b[index]));
  }
  return a.every((item, index) => item === b[index]);
}

export function useObjectSelector<T>(
  selector: (state: RootState) => T | undefined,
  properties?: (keyof T)[],
): T | undefined {
  return useAppSelector(selector, (a, b) => objectEqualityFn(a, b, properties));
}

export function useArraySelector<T>(
  selector: (state: RootState) => T[],
  properties?: (keyof T)[],
): T[] {
  return useAppSelector(selector, (a, b) => arrayEqualityFn(a, b, properties));
}
